import stringify from "fast-json-stable-stringify";
import { action, makeObservable, observable } from "mobx";

import type { ICache } from "@ihr-radioedit/inferno-core";
import { ILog, Microsite, RequestProps, ResolvedBlockInterface, sdkOpts, Site } from "@ihr-radioedit/inferno-core";
import type { PageFragment } from "@ihr-radioedit/inferno-webapi";
import { IPage } from "../abstracts/store/base-page.abstract";
import { BaseStore, CcpaStatus, IStore } from "../abstracts/store/base-store.abstract";
import { isIHRSession, isPWSSession } from "../auth/backend";
import { PWSSessionState } from "../auth/backends/pws";
import { AdPlacement } from "../lib/ads";
import { DefaultCategoriesCatalogResponse } from "../lib/default-category-type";
import { Tags } from "../lib/tagging-type";
import { GoogleMapsLibLoader } from "../loaders/google-maps-lib-loader";
import type { ABTestConfig } from "../services/variant-test";
import { StoreBlock } from "./block";
import { StoreDevice } from "./device";
import { StorePage } from "./page";
import { StorePlayer } from "./player";
import { StoreSession } from "./session";
import { normalizeStoreString } from "../utilities/store";
import { SessionBackend, IHRSessionState } from "../decoders/ihr-user";

const log = ILog.logger("Store");

/** Add new members to Base Abstract */
export class Store extends BaseStore {
  site: Site;

  @observable
  tags!: Tags;

  @observable
  adPlacements!: Map<string, AdPlacement>;

  @observable
  testGroups?: ABTestConfig;
  @observable
  request!: RequestProps;
  cache!: Map<string, ICache>;

  @observable
  page!: StorePage;
  @observable
  device!: StoreDevice;
  @observable
  player!: StorePlayer;

  @observable
  gptReady = false;

  constructor(initialData: IStore) {
    super();
    makeObservable(this);
    this.site = new Site(initialData.site.site, initialData.site.micrositeReferences);
    if (initialData.microsite) {
      this.microsite = new Microsite(initialData.microsite.microsite);
    }
    this.env = initialData.env;
    this.request = initialData.request;
    this.cache = observable.map(initialData.cache);
    this.adPlacements = observable.map(initialData.adPlacements);
    this.api_base_uri = initialData.api_base_uri;
    this.testGroups = initialData.testGroups ?? undefined;
    this.device = new StoreDevice(initialData.request.userAgent);
    this.player = new StorePlayer();
    this.block = new StoreBlock(initialData.block?.currentBlock);
    this.session = new StoreSession(initialData.session?.currentSession ?? null);
    this.page = new StorePage(initialData.page || ({} as IPage));
    this.disableThirdParty = !!initialData.disableThirdParty;
    this.defaultCategoryCatalog = initialData.defaultCategoryCatalog ?? undefined;

    this.tags = initialData.tags
      ? {
          ...initialData.tags,
          metadata: new Map(initialData.tags.metadata),
          surrogateKeys: new Set<string>(initialData.tags.surrogateKeys),
        }
      : {
          ttls: null,
          surrogateKeys: new Set<string>(),
          ads: null,
          analytics: null,
          metadata: new Map(),
        };
  }

  @action
  setGptReady = (isReady: boolean) => {
    this.gptReady = isReady;
  };

  @action
  storeTags = (tags: Tags) => {
    this.tags = tags;
  };

  @action
  storeRedirect = (url: string, status: 301 | 302 = 302) => {
    log.debug(`${status} redirect to ${url}`);
    this.redirect = { url, status };
  };

  @action
  storeBlock = (block: ResolvedBlockInterface) => {
    const incomingBlockStringified = normalizeStoreString(stringify(block));
    const currBlockStringified = this.block.currentBlock
      ? normalizeStoreString(stringify(this.block.currentBlock))
      : "";

    // Only store block if it's primary and hasn't been stored yet
    try {
      if (
        this.page.currentPage &&
        this.primaryBlockId === block.id &&
        (!this.block.currentBlock || incomingBlockStringified !== currBlockStringified)
      ) {
        this.block.storeBlock(block);
      }
    } catch (error) {
      log.warn("Error storing block", error);
    }
  };

  @action
  storePrimaryBlockId = (primaryBlockId: string) => {
    this.primaryBlockId = primaryBlockId;
  };

  @action
  storePage = (page: PageFragment | null) => {
    this.page.storePage(page);
  };

  @action
  storeSession = (session: SessionBackend) => {
    this.session.storeSession(session);
  };

  @action
  storeMicrosite = (microsite: Microsite) => {
    this.microsite = microsite;
  };

  @action
  clearMicrosite = () => {
    this.microsite = undefined;
  };

  @action
  getGoogleMaps() {
    if (!this.googleMaps) {
      this.googleMaps = new GoogleMapsLibLoader(this.env.GOOGLE_MAPS_API_KEY);
    }
    return this.googleMaps;
  }

  @action
  storeRouteParams = (params: Record<string, unknown>) => {
    this.routeParams = params;
  };

  get isIHRSession() {
    if (this.session.currentSession?.authenticated && isIHRSession(this.session.currentSession.state)) {
      const compliance = (this.session.currentSession.state as IHRSessionState)?.profile?.privacy;
      return compliance?.hasOptedOut ? CcpaStatus.OptOut : CcpaStatus.OptIn;
    }
  }

  get isPWSSession() {
    if (this.session.currentSession?.authenticated && isPWSSession(this.session.currentSession.state)) {
      const compliance = !!(this.session.currentSession.state as unknown as PWSSessionState)?.profile?.privacy_optout;
      return compliance ? CcpaStatus.OptOut : CcpaStatus.OptIn;
    }
  }

  checkBrowserGPC = () => {
    const browserGpc = (navigator as any).globalPrivacyControl;
    return browserGpc === true || browserGpc === "1";
  };

  profilePrivacyComplianceStatus = () => {
    const status = CcpaStatus.Anonymous;

    const ihrStatus = this.isIHRSession;
    if (ihrStatus) {
      return ihrStatus;
    }

    const pwsStatus = this.isPWSSession;
    if (pwsStatus) {
      return pwsStatus;
    }

    return status;
  };

  // INFO: This method should ONLY be called client-side
  getCcpaStatus = () => {
    // Access oneTrustReady to register MobX observable - Item.component.tsx
    const ready = this.oneTrustReady;

    const ampStatus = this.profilePrivacyComplianceStatus();

    // Priority order: GPC (browser) → AMP (profile) → OneTrust (SDK)
    // Check GPC and AMP first as they take precedence over OneTrust (its NOT a reliable SDK)
    if (this.checkBrowserGPC() || ampStatus === CcpaStatus.OptOut) {
      return CcpaStatus.OptOut;
    }

    // If OneTrust not ready (timeout/error in client/index.tsx), default to opted out for privacy compliance
    if (!ready) {
      return CcpaStatus.OptOut;
    }

    // OneTrust ready, check if its opted out
    if (this.oneTrust?.isOptOut()) {
      return CcpaStatus.OptOut;
    }

    // ..returns optIn or anonymous
    return ampStatus;
  };

  @action
  storeCacheValue = (key: string, data: ICache) => {
    if (key && data) {
      this.cache.set(key, data);
    }
    return this.cache;
  };

  @action
  getCacheValue = (key: string) => {
    return this.cache.get(key);
  };

  hasCacheValue = (key: string) => {
    return this.cache && this.cache.has(key);
  };

  @action
  storeStackedNavState = (stacked: boolean) => {
    this.stackedNavState = stacked;
    return this.stackedNavState;
  };

  @action
  storeAdPlacement = (key: string, data: AdPlacement) => {
    if (key && data) {
      this.adPlacements.set(key, data);
    }
    return this.cache;
  };

  @action
  getAdPlacement = (key: string) => {
    return this.adPlacements.get(key);
  };

  @action
  removeAdPlacement = (key: string) => {
    return this.hasAdPlacement(key) && this.adPlacements.delete(key);
  };

  trackPageViews = () => {
    this.igloo?.trackPageView();
    this.comscore?.trackPageView();
  };

  hasAdPlacement = (key: string) => {
    return this.adPlacements && this.adPlacements.has(key);
  };

  getSdkOpts = () => (this.sdkOpts = sdkOpts());

  getDefaultCategoryCatalog() {
    return this.defaultCategoryCatalog;
  }

  setDefaultCategoryCatalog(data: DefaultCategoriesCatalogResponse) {
    this.defaultCategoryCatalog = data;
  }
}
